import { pruefe, sb } from "./daten";
import type { Partei } from "./kern/eRechnung";

const FREEMAIL = /^(gmail|googlemail|gmx|web|t-online|yahoo|outlook|hotmail|icloud|me|mac|aol|freenet|posteo|mailbox|protonmail|proton)\./i;

export type FirmaKurz = { id: number; name: string; website: string | null; tax_identifier: string | null };

const domainVon = (email: string | null) => {
  const d = email?.split("@")[1]?.toLowerCase().trim();
  return d && !FREEMAIL.test(d) ? d : null;
};
const websiteDomain = (w: string | null) =>
  (w ?? "").toLowerCase().replace(/^https?:\/\/(www\.)?/, "").split(/[/:?#]/)[0] || null;
// „Akkus Kreuzberg Gmbh (WEST)“ und „AKKUS Kreuzberg GmbH“ gelten als dieselbe Firma
export const nameNormal = (n: string) =>
  n
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/\b(gmbh|ug|ag|kg|ohg|e\.?\s?k\.?|mbh|co|haftungsbeschränkt|ltd|inc|bv|b\.v\.|s\.r\.o\.|sa|sas|srl)\b/g, " ")
    .replace(/[^a-z0-9äöüß]+/g, " ")
    .trim();

export const firmaFinden = (p: Partei, firmen: FirmaKurz[]) => {
  const domain = domainVon(p.email);
  const ust = p.ustId?.replace(/\s/g, "").toUpperCase();
  const name = nameNormal(p.name);
  return (
    (domain && firmen.find((f) => websiteDomain(f.website) === domain)) ||
    (ust && firmen.find((f) => f.tax_identifier?.replace(/\s/g, "").toUpperCase() === ust)) ||
    (name && firmen.find((f) => nameNormal(f.name) === name)) ||
    null
  );
};

// Eine Person nur, wenn die Rechnung einen echten Namen nennt (nicht den Firmennamen)
export const ansprechpartner = (p: Partei) => {
  const k = p.kontakt?.trim();
  if (!k || nameNormal(k) === nameNormal(p.name) || /\b(gmbh|ag|ug|kg|team|buchhaltung|service|shop|info)\b/i.test(k)) return null;
  const teile = k.split(/\s+/);
  return teile.length < 2 ? null : { first_name: teile.slice(0, -1).join(" "), last_name: teile[teile.length - 1] };
};

export const firmaSicherstellen = async (p: Partei, anlass: string) => {
  if (!p.name) return null;
  const { data } = pruefe(await sb().from("companies").select("id, name, website, tax_identifier, address, zipcode, city, phone_number"));
  const firmen = (data ?? []) as (FirmaKurz & Record<string, string | null>)[];
  const treffer = firmaFinden(p, firmen) as (FirmaKurz & Record<string, string | null>) | null;
  const felder = {
    address: p.strasse,
    zipcode: p.plz,
    city: p.ort,
    country: p.land,
    phone_number: p.telefon,
    tax_identifier: p.ustId,
  };
  let id: number;
  if (treffer) {
    // Nur leere Felder ergänzen, Gepflegtes bleibt
    const ergaenzen = Object.fromEntries(Object.entries(felder).filter(([k, v]) => v && !treffer[k]));
    if (Object.keys(ergaenzen).length) pruefe(await sb().from("companies").update(ergaenzen).eq("id", treffer.id));
    id = treffer.id;
  } else {
    const domain = domainVon(p.email);
    const { data: neu } = pruefe(
      await sb()
        .from("companies")
        .insert({
          name: p.name,
          ...Object.fromEntries(Object.entries(felder).filter(([, v]) => v)),
          website: domain ? `https://${domain}` : null,
          description: `Automatisch angelegt aus ${anlass}. Bitte Typ prüfen.`,
        })
        .select("id")
        .single(),
    );
    id = neu?.id;
  }
  const person = ansprechpartner(p);
  if (person && id) {
    const { data: vorhanden } = pruefe(
      await sb().from("contacts").select("id").eq("company_id", id).ilike("last_name", person.last_name).limit(1),
    );
    if (!vorhanden?.length) {
      pruefe(
        await sb().from("contacts").insert({
          ...person,
          company_id: id,
          email_jsonb: p.email ? [{ email: p.email, type: "Work" }] : [],
          phone_jsonb: p.telefon ? [{ number: p.telefon, type: "Work" }] : [],
          background: `Automatisch angelegt aus ${anlass}.`,
          first_seen: new Date().toISOString(),
          last_seen: new Date().toISOString(),
        }),
      );
    }
  }
  return id ?? null;
};
